import React from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { ChartWrapper } from 'src/components';
import PropTypes from 'prop-types';

import { AspectCategoryStatusIcon } from 'src/scenes/Project/scenes/PressuresAndDependencies/components';
import { aspectCategoriesStatusMapper, aspectCategoryStatusPriority } from 'src/utils/aspectCategoriesUtils';


const useLegendStyles = makeStyles(theme => ({
  legendContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(1),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  colorBox: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    marginRight: theme.spacing(1.25),
  },
  label: {
    flex: 1,
    marginRight: theme.spacing(2.5),
    marginLeft: theme.spacing(1),
  },
  value: {
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
  },
}));

const generateRandomData = total => {
  const parts = 5; // Número de categorías
  const randomValues = Array.from({ length: parts }, () => Math.random());
  const sum = randomValues.reduce((acc, val) => acc + val, 0);
  return randomValues.map(val => Math.round((val / sum) * total));
};

const CustomLegend = ({ labels, data }) => {
  const classes = useLegendStyles();

  const total = data.reduce((a, b) => a + b, 0);

  return (
    <Box className={classes.legendContainer}>
      {labels.map((label, index) => (
        <Box key={label} className={classes.legendItem}>
          <AspectCategoryStatusIcon category={label} size="small" />
          <Box className={classes.label}>{aspectCategoriesStatusMapper[label]?.text}</Box>
          <Box className={classes.value}>
            {`${data[index]} (${((data[index] / total) * 100).toFixed(1)}%)`}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const heightVH = n => document.documentElement.clientHeight * (n / 100);
const impactLabels = aspectCategoryStatusPriority.slice(0, 5).reverse();

const ProjectLevelDoughnutChart = ({ projectCount }) => {
  const data = generateRandomData(projectCount);

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Box pr={5} height={heightVH(25)}>
        <ChartWrapper
          type="doughnut"
          options={{
            maintainAspectRatio: true, // Mantiene la relación de aspecto
            aspectRatio: 1, // Relación de aspecto 1:1 (cuadrado)
            plugins: {
              legend: {
                display: false,
              },
            },
          }}
          plugins={[
            {
              id: 'centerText',
              beforeDraw: chart => {
                const { width, height } = chart;
                const ctx = chart.ctx;
                ctx.save();
                // Configuración del texto
                const fontSize = (height / 150).toFixed(2);
                ctx.font = `${fontSize}em sans-serif`;
                ctx.fillStyle = '#333'; // Color del texto
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                // Texto principal (número de planes de monitoreo)
                const text1 = `${projectCount}`;
                const textX = width / 2;
                const textY1 = height / 2 - 15; // Ajusta la posición vertical del texto principal
                ctx.fillText(text1, textX, textY1);
                // Texto secundario ("Proyectos")
                ctx.font = `${(height / 200).toFixed(2)}em sans-serif`; // Fuente más pequeña para el texto secundario
                const text2 = 'Proyectos';
                const textY2 = height / 2 + 10; // Ajusta la posición vertical del texto secundario
                ctx.fillText(text2, textX, textY2);
                ctx.restore();
              },
            },
          ]}
          data={{
            labels: impactLabels,
            datasets: [
              {
                data,
                backgroundColor: impactLabels.map(category => aspectCategoriesStatusMapper[category].borderColor),
                borderColor: impactLabels.map(category => aspectCategoriesStatusMapper[category].borderColor),
              },
            ],
          }}
        />
      </Box>
      <CustomLegend
        labels={impactLabels}
        data={data}
      />
    </Box>
  );
};

ProjectLevelDoughnutChart.propTypes = {
  projectCount: PropTypes.number.isRequired,
};

CustomLegend.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.arrayOf(PropTypes.number).isRequired,
};


export {
  ProjectLevelDoughnutChart,
};
